import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import { api } from "../api/client";
import type { AdminUser, Meta, Role } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import { ConfirmDialog, ErrorBanner, LoadingState, PageHeader, ago, when } from "../components/common";

/**
 * Admin access. Owner-only, and the server refuses the lockout cases regardless of
 * what this screen offers — a self-demotion, deactivating yourself, or removing the
 * last owner. The UI mirrors those rules so the button is off before the request
 * fails, but the API is the thing enforcing them.
 */

const ROLE_HELP: Record<Role, string> = {
  viewer: "Read everything. Cannot change anything.",
  operator: "Triage leads and administer sources, signals, markets and clients.",
  owner: "Everything, plus managing admin access and destructive deletes.",
};

export function AdminsPage() {
  const { user } = useAuth();
  const admins = useResource<{ items: AdminUser[] }>("/api/admins");
  const meta = useResource<Meta>("/api/meta");
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const setRole = useMutation(async (args: { admin: AdminUser; role: string }) => {
    await api.put(`/api/admins/${args.admin.id}`, { role: args.role });
  });
  const setActive = useMutation(async (admin: AdminUser) => {
    await api.put(`/api/admins/${admin.id}`, { active: !admin.active });
  });
  const remove = useMutation(async (admin: AdminUser) => {
    await api.del(`/api/admins/${admin.id}`);
  });

  if (admins.loading && !admins.data) return <LoadingState label="Loading admins…" />;

  const activeOwners = (admins.data?.items ?? []).filter((a) => a.role === "owner" && a.active).length;

  return (
    <Box>
      <PageHeader
        title="Admin access"
        subtitle="Who can sign in to this console, and what they may do."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            Add admin
          </Button>
        }
      />

      <ErrorBanner error={admins.error} onRetry={admins.reload} />
      <ErrorBanner error={setRole.error ?? setActive.error ?? remove.error} />

      {activeOwners === 1 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          There is one active owner. Add a second before anyone needs to hand over — the last owner cannot be
          demoted or removed.
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Last sign-in</TableCell>
              <TableCell>Added</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(admins.data?.items ?? []).map((admin) => {
              const isSelf = admin.id === user?.id;
              const isLastOwner = admin.role === "owner" && admin.active && activeOwners === 1;
              return (
                <TableRow key={admin.id} hover>
                  <TableCell>
                    {admin.email}
                    {isSelf ? <Chip label="you" sx={{ ml: 1 }} variant="outlined" /> : null}
                  </TableCell>
                  <TableCell>{admin.name ?? "—"}</TableCell>
                  <TableCell>
                    <Tooltip title={ROLE_HELP[admin.role] ?? admin.role}>
                      <TextField
                        select
                        value={admin.role}
                        disabled={isSelf || isLastOwner || setRole.pending}
                        onChange={(e) =>
                          void setRole.run({ admin, role: e.target.value }).then((ok) => {
                            if (ok) {
                              setToast(`${admin.email} is now ${e.target.value}`);
                              admins.reload();
                            }
                          })
                        }
                        sx={{ minWidth: 130 }}
                      >
                        {(meta.data?.roles ?? ["viewer", "operator", "owner"]).map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{ago(admin.lastLoginAt)}</TableCell>
                  <TableCell>{when(admin.createdAt)}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        isSelf
                          ? "You cannot deactivate your own account"
                          : isLastOwner
                            ? "The last active owner cannot be deactivated"
                            : ""
                      }
                    >
                      <Switch
                        checked={admin.active}
                        disabled={isSelf || isLastOwner || setActive.pending}
                        onChange={() =>
                          void setActive.run(admin).then((ok) => {
                            if (ok) {
                              setToast(`${admin.email} ${admin.active ? "deactivated" : "reactivated"}`);
                              admins.reload();
                            }
                          })
                        }
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Set a new password (signs them out everywhere)">
                        <IconButton onClick={() => setResetting(admin)}>
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isSelf ? "You cannot delete your own account" : "Delete admin"}>
                        <IconButton
                          color="error"
                          disabled={isSelf || isLastOwner}
                          onClick={() => setDeleting(admin)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Passwords are stored as scrypt hashes and are never returned by the API. A locked-out owner can be
        recovered from the CLI: <code>seed-admin &lt;email&gt; owner</code>.
      </Typography>

      {creating ? (
        <AdminDialog
          roles={meta.data?.roles ?? ["viewer", "operator", "owner"]}
          onClose={() => setCreating(false)}
          onSaved={(email) => {
            setCreating(false);
            setToast(`Created ${email}`);
            admins.reload();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPasswordDialog
          admin={resetting}
          onClose={() => setResetting(null)}
          onSaved={() => {
            const email = resetting.email;
            setResetting(null);
            setToast(`Password reset for ${email}`);
            admins.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.email}?`}
        pending={remove.pending}
        body="They lose access immediately. Their entries in the audit log are kept."
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void remove.run(deleting).then((ok) => {
            if (!ok) return;
            setToast(`Deleted ${deleting.email}`);
            setDeleting(null);
            admins.reload();
          });
        }}
      />

      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""} />
    </Box>
  );
}

const MIN_LENGTH = 12;

function AdminDialog({
  roles,
  onClose,
  onSaved,
}: {
  roles: Role[];
  onClose: () => void;
  onSaved: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [password, setPassword] = useState("");

  const save = useMutation(async () => {
    await api.post("/api/admins", {
      email: email.trim(),
      name: name.trim() === "" ? null : name.trim(),
      role,
      password,
    });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add admin</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            helperText={ROLE_HELP[role]}
            fullWidth
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Initial password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            error={password !== "" && password.length < MIN_LENGTH}
            helperText={`At least ${MIN_LENGTH} characters. Send it to them over a channel you trust; they can change it after signing in.`}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={email.trim() === "" || password.length < MIN_LENGTH || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved(email.trim()))}
        >
          {save.pending ? "Saving…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResetPasswordDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState("");
  const save = useMutation(async () => {
    await api.put(`/api/admins/${admin.id}`, { password });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password — {admin.email}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          This signs {admin.email} out of every session immediately.
        </Alert>
        <TextField
          label="New password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          error={password !== "" && password.length < MIN_LENGTH}
          helperText={`At least ${MIN_LENGTH} characters.`}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={password.length < MIN_LENGTH || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : "Reset password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
